import { useCallback, useContext, useMemo } from 'react';
import {
  type ErrorInfo,
  NotificationContext,
  type NotifyTypeProps,
} from '../components/NotifyProvider';

type Notify = {
  notification: NotifyTypeProps | null;
  showError: (message: string, errorDetails?: ErrorInfo) => void;
  showSuccess: (message: string, clearAfter?: number) => void;
  showWarning: (message: string) => void;
  removeNotification: () => void;
};

const useNotify = (): Notify => {
  const { notification, notify, removeNotification } =
    useContext(NotificationContext);

  const showError = useCallback(
    (message: string, errorDetails?: ErrorInfo) => {
      notify({ message, type: 'error', errorDetails });
    },
    [notify]
  );

  const showSuccess = useCallback(
    (message: string, clearAfter: number = 4000) => {
      notify({ type: 'success', message }, clearAfter);
    },
    [notify]
  );

  const showWarning = useCallback(
    (message: string) => {
      notify({ type: 'warn', message });
    },
    [notify]
  );
  return useMemo(
    () => ({
      notification,
      showError,
      showSuccess,
      showWarning,
      removeNotification,
    }),
    [notification, showError, showSuccess, showWarning, removeNotification]
  );
};

export default useNotify;
